import { useEffect, useMemo, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, EmptyState, Btn } from "@/components/primitives";
import { initials } from "@/lib/format";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Pencil, Trash2, Search, Loader2, Lock, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const inputCls = "w-full h-10 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green focus:ring-2 focus:ring-curtron-green/20 transition";
const labelCls = "text-[11px] uppercase tracking-[0.05em] font-medium text-curtron-sub";
const ROLE_CLS = { admin: "bg-curtron-green/12 text-curtron-green", driver: "bg-curtron-info/12 text-curtron-info", dispatcher: "bg-curtron-ochre/18 text-curtron-ochre" };
const EMPTY = { name: "", email: "", password: "", role: "driver", region: "", van_number: "" };
// Only this account may create/delete accounts or change roles (admin/driver/staff status).
const PRIMARY_ADMIN_EMAIL = "admin@curtronhub.com";

export default function UsersPage() {
  const { user: me } = useAuth();
  const amPrimary = (me?.email || "").toLowerCase() === PRIMARY_ADMIN_EMAIL;
  const isPrimaryAccount = (u) => (u?.email || "").toLowerCase() === PRIMARY_ADMIN_EMAIL;
  const [users, setUsers] = useState(null);
  const [regions, setRegions] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([])), []);
  useEffect(() => {
    load();
    api.get("/regions").then((r) => setRegions(r.data)).catch(() => {});
  }, [load]);

  const roleOptions = useMemo(() => {
    // Backend only accepts these role values.
    const s = new Set(["admin", "driver"]);
    (users || []).forEach((u) => u.role && s.add(u.role));
    return [...s];
  }, [users]);

  const filtered = useMemo(() => {
    if (!users) return [];
    const t = q.trim().toLowerCase();
    const list = t ? users.filter((u) => [u.name, u.email, u.role].filter(Boolean).some((v) => v.toLowerCase().includes(t))) : users;
    return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, q]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ name: u.name || "", email: u.email || "", password: "", role: u.role || "driver", region: u.region || "", van_number: u.van_number || "" }); setEditId(u.id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    const original = editId ? (users || []).find((u) => u.id === editId) : null;
    if (original && isPrimaryAccount(original) && !amPrimary) { toast.error("The main admin account is protected and can't be modified."); return; }
    if (original && original.role === "admin" && form.role === "driver") {
      const adminCount = (users || []).filter((u) => u.role === "admin").length;
      if (adminCount <= 1) { toast.error("Cannot demote the last remaining admin."); return; }
    }
    setSaving(true);
    try {
      if (editId) {
        const payload = { name: form.name.trim(), email: form.email.trim().toLowerCase(), role: form.role, region: form.region || null, van_number: form.van_number || null };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editId}`, payload);
        toast.success("User updated");
      } else {
        const { data: created } = await api.post("/users", { name: form.name.trim(), email: form.email.trim().toLowerCase(), password: form.password, role: form.role, region: form.region || null });
        if (form.van_number && created?.id) {
          await api.patch(`/users/${created.id}`, { name: form.name.trim(), email: form.email.trim().toLowerCase(), role: form.role, region: form.region || null, van_number: form.van_number });
        }
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    const target = (users || []).find((u) => u.id === id);
    if (target && isPrimaryAccount(target) && !amPrimary) { toast.error("The main admin account is protected and can't be deleted."); return; }
    const adminCount = (users || []).filter((u) => u.role === "admin").length;
    if (target?.role === "admin" && adminCount <= 1) { toast.error("You can't delete the last remaining admin."); return; }
    try { await api.delete(`/users/${id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (users === null) return <Loading />;

  return (
    <div>
      <PageHeader title="Manage Users" subtitle="Drivers, roles & regions.">
        <Btn onClick={openNew} data-testid="new-user-btn"><Plus className="w-4 h-4" /> Add User</Btn>
      </PageHeader>

      {amPrimary ? (
        <div className="flex items-center gap-2 mb-4 text-xs text-curtron-green bg-curtron-green/8 border border-curtron-green/20 rounded-md px-3 py-2" data-testid="primary-admin-banner">
          <ShieldCheck className="w-4 h-4 shrink-0" /> You are the primary administrator. Your admin account is protected — other admins can't modify or remove it.
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-4 text-xs text-curtron-sub bg-curtron-bg border border-curtron-line rounded-md px-3 py-2" data-testid="restricted-admin-banner">
          <Lock className="w-4 h-4 shrink-0 text-curtron-muted" /> You can manage all users and roles. The primary admin account is protected and can't be modified or removed.
        </div>
      )}

      <Card className="p-3 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-curtron-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" placeholder="Search users…"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-curtron-line bg-curtron-bg text-sm outline-none focus:border-curtron-green focus:bg-curtron-surface transition-colors" />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card><EmptyState icon={UsersIcon} title="No users" /></Card>
      ) : (
        <Card className="divide-y divide-curtron-line">
          {filtered.map((u) => (
            <div key={u.id} className="flex items-center gap-3 px-4 py-3.5" data-testid={`user-row-${u.id}`}>
              {u.avatar ? <img src={u.avatar} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" /> : <div className="w-10 h-10 rounded-full bg-curtron-moss/25 text-curtron-green flex items-center justify-center text-sm font-semibold shrink-0">{initials(u.name)}</div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-curtron-ink text-sm truncate">{u.name}{u.id === me?.id && <span className="text-xs text-curtron-muted ml-1">(you)</span>}</span>
                  <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${ROLE_CLS[u.role] || "bg-curtron-line text-curtron-sub"}`}>{u.role}</span>
                  {u.sms_opt_in && <span className="inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium bg-curtron-green/12 text-curtron-green">SMS ON</span>}
                </div>
                <div className="text-xs text-curtron-muted truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                <div className="text-xs text-curtron-muted truncate">{regions.find((r) => r.key === u.region)?.label || "No region"}{u.van_number ? ` · ${u.van_number}` : ""}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {isPrimaryAccount(u) && !amPrimary ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-curtron-muted px-2" data-testid={`user-protected-${u.id}`}><Lock className="w-3.5 h-3.5" /> Protected</span>
                ) : (
                  <>
                    <Btn variant="ghost" onClick={() => openEdit(u)} className="h-8 px-2" data-testid={`user-edit-${u.id}`}><Pencil className="w-4 h-4" /></Btn>
                    {u.id !== me?.id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Btn variant="ghost" className="h-8 px-2 text-curtron-terracotta" data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></Btn></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Remove {u.name}?</AlertDialogTitle><AlertDialogDescription>This user will lose access. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(u.id)} className="bg-curtron-terracotta hover:bg-[#A35549]">Remove</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>Manage team member access, role, and region.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className={labelCls}>Full Name *</label><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name" className={`${inputCls} mt-1`} /></div>
            <div><label className={labelCls}>Email *</label><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email" className={`${inputCls} mt-1`} /></div>
            <div><label className={labelCls}>{editId ? "New Password (optional)" : "Password *"}</label><input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password" className={`${inputCls} mt-1`} placeholder={editId ? "Leave blank to keep current" : ""} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Role</label>
                <select value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="user-role" className={`${inputCls} mt-1`}>
                  {roleOptions.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Region</label>
                <select value={form.region || ""} onChange={(e) => set("region", e.target.value)} data-testid="user-region" className={`${inputCls} mt-1`}>
                  <option value="">None</option>
                  {regions.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Van Number</label>
              <input value={form.van_number} onChange={(e) => set("van_number", e.target.value)} data-testid="user-van" placeholder="e.g. Van 7" className={`${inputCls} mt-1`} />
              <p className="text-[10px] text-curtron-muted mt-1">Auto-fills on transports assigned to this driver.</p>
            </div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn onClick={save} disabled={saving} data-testid="user-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
